import { isPlatformServer } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { shouldLog } from '../utils/logging';
import { AuthService } from './auth.service';

/**
 * Allows activation only for signed-in users.
 *
 * On the server every route is allowed (auth is a browser-only concern and the
 * page is re-guarded after hydration). In the browser it waits for the initial
 * auth state to resolve, then redirects unauthenticated users to the login page.
 */
export const authGuard: CanActivateFn = async (_route, state) => {
  const platformId = inject(PLATFORM_ID);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (isPlatformServer(platformId)) {
    return true;
  }

  const loggedIn = await authService.whenResolved();
  if (shouldLog()) {
    console.log(`[auth-guard] loggedIn=${loggedIn}`);
  }

  if (loggedIn) {
    return true;
  }
  // Carry the attempted URL so login can bounce back — the login page already
  // honors a `pathname` query param. This matters most when auth resolution
  // timed out (watchdog) for a genuinely signed-in user: Firebase's late
  // emission logs them in and the stashed pathname restores where they were.
  return router.createUrlTree(['/user/login'], { queryParams: { pathname: state.url } });
};
